import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Mail, ListChecks, ChefHat, CalendarCheck, HandHeart, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { fetchSubmissionCounts, fetchSubmissions, updateSubmissionStatus, deleteSubmission } from "../../lib/api";

// Where each submission stands. Older submissions without a status count as New.
const STATUSES = [
  { value: "new", label: "New", cls: "border-[#E8490F]/60 bg-[#E8490F]/15 text-[#ff8a5c]" },
  { value: "contacted", label: "Contacted", cls: "border-sky-400/40 bg-sky-400/10 text-sky-300" },
  { value: "in_progress", label: "In progress", cls: "border-amber-400/40 bg-amber-400/10 text-amber-300" },
  { value: "converted", label: "Converted", cls: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" },
  { value: "closed", label: "Closed", cls: "border-white/15 bg-white/5 text-white/50" },
];
const statusOf = (s) => STATUSES.find((x) => x.value === (s.status || "new")) || STATUSES[0];

// One entry per form. `fields` lists what to show on each submission, in order.
const INBOXES = [
  {
    key: "contact",
    endpoint: "contact",
    label: "Contact",
    icon: Mail,
    title: (s) => s.name,
    fields: [
      ["Platform", "platform_of_interest"],
      ["Restaurant", "restaurant_name"],
      ["City", "city"],
      ["Message", "message"],
    ],
  },
  {
    key: "waitlist",
    endpoint: "waitlist",
    label: "Waitlist",
    icon: ListChecks,
    title: (s) => s.email,
    fields: [["Source", "source"]],
  },
  {
    key: "hoh_pilot",
    endpoint: "hoh-pilot",
    label: "Heart of House pilot",
    icon: ChefHat,
    title: (s) => `${s.name} · ${s.restaurant_name}`,
    fields: [
      ["Role", "role"],
      ["City", "city"],
      ["Type of place", "place_type"],
      ["Type of place (other)", "place_type_other"],
      ["Covers per week", "covers"],
      ["Seats", "seats"],
      ["Current POS", "current_pos"],
      ["POS (other)", "pos_other"],
      ["Costing today", "costing"],
      ["Costing app", "costing_app"],
      ["Updates from Mise", "updates_answer"],
      ["Monthly revenue", "monthly_revenue"],
      ["Food cost %", "food_cost_pct"],
      ["Timeline", "timeline"],
      ["Notes", "notes"],
    ],
  },
  {
    key: "hoh_demo",
    endpoint: "hoh-demo",
    label: "Heart of House demo",
    icon: CalendarCheck,
    title: (s) => (s.restaurant_name ? `${s.name} · ${s.restaurant_name}` : s.name),
    fields: [
      ["Role", "role"],
      ["Tier", "tier"],
      ["Current POS", "pos"],
      ["POS (other)", "pos_other"],
      ["Biggest BOH pain", "pain_point"],
      ["Availability", "availability"],
      ["Heard about us", "referral"],
      ["Heard about us (other)", "referral_other"],
    ],
  },
  {
    key: "family_meals",
    endpoint: "family-meals-inquiry",
    label: "Family Meals",
    icon: HandHeart,
    title: (s) =>
      `${s.contact_name} · ${s.business_name || s.organization_name || (s.role === "supplier" ? "Supplier" : "Distributor")}`,
    fields: [
      ["Role", "role"],
      ["Business type", "business_type"],
      ["Location", "location"],
      ["Surplus volume", "surplus_volume"],
      ["Surplus notes", "surplus_notes"],
      ["Organization type", "organization_type"],
      ["Service area", "service_area"],
      ["Capacity", "capacity"],
      ["Community needs", "community_needs"],
    ],
  },
];

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const SubmissionList = ({ token, inbox, onClose }) => {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [confirming, setConfirming] = useState(null);
  const listKey = ["submissions", inbox.endpoint, token];
  const { data, isLoading, error } = useQuery({
    queryKey: listKey,
    queryFn: () => fetchSubmissions(token, inbox.endpoint),
    staleTime: 30000,
    retry: false,
  });
  const refreshCounts = () => queryClient.invalidateQueries({ queryKey: ["submission-counts"] });

  const setStatus = useMutation({
    mutationFn: ({ id, status }) => updateSubmissionStatus(token, inbox.endpoint, id, status),
    onSuccess: ({ id, status }) => {
      queryClient.setQueryData(listKey, (old) => (old || []).map((s) => (s.id === id ? { ...s, status } : s)));
      refreshCounts();
    },
    onError: () => toast.error("Couldn't change the status. Try again."),
  });

  const remove = useMutation({
    mutationFn: (id) => deleteSubmission(token, inbox.endpoint, id),
    onSuccess: (_res, id) => {
      queryClient.setQueryData(listKey, (old) => (old || []).filter((s) => s.id !== id));
      setConfirming(null);
      refreshCounts();
      toast.success("Submission deleted");
    },
    onError: () => toast.error("Couldn't delete it. Try again."),
  });

  const shown = (data || []).filter((s) => filter === "all" || (s.status || "new") === filter);

  return (
    <div className="mise-card p-6 mt-4" data-testid={`submissions-${inbox.key}`}>
      <div className="flex items-center justify-between gap-4 mb-2">
        <h3 className="text-lg font-semibold">{inbox.label}</h3>
        <div className="flex items-center gap-2">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="rounded-lg border border-[#444444] bg-[#1a1a1a] px-2.5 py-1.5 text-xs text-white/85 outline-none focus:border-[#E8490F]"
          data-testid={`submissions-filter-${inbox.key}`}
        >
          <option value="all">All statuses ({(data || []).length})</option>
          {STATUSES.map((st) => (
            <option key={st.value} value={st.value}>
              {st.label} ({(data || []).filter((s) => (s.status || "new") === st.value).length})
            </option>
          ))}
        </select>
        <button
          onClick={onClose}
          className="p-2 rounded-lg bg-[#242424] border border-[#444444] text-white/70 hover:text-white transition-colors"
          title="Close"
        >
          <X size={14} />
        </button>
        </div>
      </div>

      {isLoading && <p className="text-white/50 text-sm py-4">Loading…</p>}
      {error && <p className="text-red-400 text-sm py-4">Couldn't load submissions.</p>}
      {data && data.length === 0 && <p className="text-white/50 text-sm py-4">No submissions yet.</p>}

      {data && data.length > 0 && shown.length === 0 && (
        <p className="text-white/50 text-sm py-4">Nothing with this status.</p>
      )}

      {shown.length > 0 && (
        <ul className="divide-y divide-[#333333]">
          {shown.map((s) => (
            <li key={s.id} className="py-5" data-testid={`submission-${s.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                  <p className="font-medium text-white">{inbox.title(s)}</p>
                  <p className="text-xs text-white/40 whitespace-nowrap">{fmtDate(s.created_at)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={statusOf(s).value}
                    onChange={(e) => setStatus.mutate({ id: s.id, status: e.target.value })}
                    className={`rounded-full border px-3 py-1 text-xs font-medium outline-none ${statusOf(s).cls}`}
                    aria-label="Status"
                    data-testid="submission-status"
                  >
                    {STATUSES.map((st) => (
                      <option key={st.value} value={st.value} className="bg-[#1a1a1a] text-white">{st.label}</option>
                    ))}
                  </select>
                  {confirming === s.id ? (
                    <span className="flex items-center gap-1.5 text-xs">
                      <span className="text-white/60">Delete for good?</span>
                      <button
                        onClick={() => remove.mutate(s.id)}
                        disabled={remove.isPending}
                        className="rounded-full bg-red-600 px-3 py-1 font-medium text-white hover:bg-red-500 disabled:opacity-60"
                        data-testid="submission-delete-confirm"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => setConfirming(null)}
                        className="rounded-full border border-[#444444] px-3 py-1 text-white/70 hover:text-white"
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <button
                      onClick={() => setConfirming(s.id)}
                      className="rounded-lg border border-[#444444] p-1.5 text-white/50 hover:border-red-500/60 hover:text-red-400"
                      title="Delete"
                      data-testid="submission-delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
              <p className="mt-1 text-sm text-white/70 flex flex-wrap gap-x-4 gap-y-1">
                <a href={`mailto:${s.email}`} className="text-[#E8490F] hover:underline">{s.email}</a>
                {s.phone && <a href={`tel:${s.phone}`} className="hover:underline">{s.phone}</a>}
              </p>
              <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-[max-content_1fr] text-sm">
                {inbox.fields
                  .filter(([, k]) => s[k])
                  .map(([label, k]) => (
                    <React.Fragment key={k}>
                      <dt className="text-white/45">{label}</dt>
                      <dd className="text-white/85 whitespace-pre-line break-words">{s[k]}</dd>
                    </React.Fragment>
                  ))}
              </dl>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const FormInboxes = ({ token }) => {
  const [open, setOpen] = useState(null);
  const { data: counts } = useQuery({
    queryKey: ["submission-counts", token],
    queryFn: () => fetchSubmissionCounts(token),
    refetchInterval: 60000,
    retry: false,
  });
  const openInbox = INBOXES.find((i) => i.key === open);

  return (
    <section className="mb-8" data-testid="form-inboxes">
      <h2 className="text-xs text-white/50 uppercase tracking-wider mb-3">Form submissions</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {INBOXES.map((inbox) => {
          const Icon = inbox.icon;
          const active = open === inbox.key;
          return (
            <button
              key={inbox.key}
              onClick={() => setOpen(active ? null : inbox.key)}
              aria-expanded={active}
              className={`mise-card p-5 flex items-center gap-4 text-left transition-colors ${
                active ? "border-[#E8490F]" : "hover:border-[#E8490F]/40"
              }`}
              data-testid={`inbox-card-${inbox.key}`}
            >
              <div className="p-2.5 rounded-xl bg-[#E8490F]/10">
                <Icon size={20} className="text-[#E8490F]" />
              </div>
              <div>
                <p className="text-xs text-white/50 uppercase tracking-wider">{inbox.label}</p>
                <p className="text-2xl font-semibold tracking-tight mt-0.5">
                  {counts ? counts[inbox.key] : "-"}
                  {counts?.new?.[inbox.key] > 0 && (
                    <span className="ml-2 align-middle rounded-full bg-[#E8490F]/15 px-2 py-0.5 text-xs font-medium text-[#ff8a5c]">
                      {counts.new[inbox.key]} new
                    </span>
                  )}
                </p>
              </div>
            </button>
          );
        })}
      </div>
      {openInbox && <SubmissionList token={token} inbox={openInbox} onClose={() => setOpen(null)} />}
    </section>
  );
};

export default FormInboxes;
